import type { ReactNode } from 'react'
import type { Lookup, UserRef } from '../api/types'
import { withAlpha } from '../lib/format'
import { useUserPhoto } from '../lib/photos'

/** Statusmarkering med den farve der er sat på statussen i adminpanelet. */
export function StatusPill({ status }: { status: Lookup }) {
  return (
    <span className="pill" style={{ background: withAlpha(status.color, 0.14), color: status.color }}>
      <span className="pill__dot" />
      {status.name}
    </span>
  )
}

export function PriorityPill({ priority }: { priority: Lookup }) {
  return (
    <span className="pill" style={{ background: withAlpha(priority.color, 0.14), color: priority.color }}>
      {priority.name}
    </span>
  )
}

export function ProgressBar({ value, color }: { value: number; color?: string }) {
  return (
    <div className="progress" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className="progress__bar" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color ?? '#2563EB' }} />
    </div>
  )
}

/**
 * Profilbillede med initialer som fallback.
 * Billedet hentes med authentication (se lib/photos.ts), da det ligger bag API-et.
 */
export function Avatar({ user, title, size }: { user?: UserRef | null; title?: string; size?: number }) {
  const photo = useUserPhoto(user?.photoUrl)
  const style = size ? { width: size, height: size, fontSize: Math.round(size / 2.4) } : undefined

  if (!user) {
    return (
      <span className="avatar" style={{ ...style, background: '#94A3B8' }} title={title ?? 'Ingen ansvarlig'}>
        ?
      </span>
    )
  }

  if (photo) {
    return (
      <img
        className="avatar avatar--photo"
        style={style}
        src={photo}
        alt=""
        title={title ?? user.displayName}
      />
    )
  }

  return (
    <span className="avatar" style={style} title={title ?? user.displayName}>
      {user.initials || user.displayName.slice(0, 1).toUpperCase()}
    </span>
  )
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>
}

export function ErrorBox({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Der skete en uventet fejl.'
  return <div className="error-box">{message}</div>
}

export function Loading({ text = 'Henter data ...' }: { text?: string }) {
  return <div className="empty">{text}</div>
}

export function Card({ title, actions, children }: { title?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card__header">
          {title && <h2 className="card__title">{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

export function Section({ title, count, actions, children }: { title: string; count?: number; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <header className="section__header">
        <h2 className="section__title">{title}</h2>
        {count !== undefined && <span className="section__count">{count}</span>}
        <span style={{ marginLeft: 'auto' }}>{actions}</span>
      </header>
      {children}
    </section>
  )
}
